import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { IonPopover, IonIcon } from '@ionic/react';
import { menuController } from '@ionic/core/components';
import { logOutOutline, bookOutline, cubeOutline, cardOutline, trendingUpOutline, keyOutline, settingsOutline, copyOutline, checkmarkOutline } from 'ionicons/icons';
import DiscordLogo from '../components/atoms/DiscordLogo/DiscordLogo';
import AuthService from '../services/auth';
import Credits from '../services/credits';
import SettingsModal from '../components/organisms/Modals/SettingsModal/SettingsModal';
import { routesPath, ChatContext } from '../App';
// Header credit chip removed — balance is shown on /billing instead.
// The chip looked broken (red "LOW" tag + small wallet glyph misrendering)
// and duplicated the balance hero on BillingPage.

import { projectIdFromPathname, projectMarketplacePathForLocation } from '../utils/marketplacePaths';
import './Header.css';

// Custom sidebar icon component
const SidebarIcon = () => (
<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-three-dots" viewBox="0 0 16 16">
  <path d="M3 9.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3m5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3m5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3"/>
</svg>
);

// Logo component using the actual logo image
const Logo = ({ onClick }) => (
  <div className="logo-container" onClick={onClick} style={{ cursor: 'pointer' }}>
    <img 
      src="/shaded_cube_logo.svg" 
      alt="Logo" 
      className="logo-image"
    />
  </div>
);

const Header = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: routeProjectId } = useParams();
  const projectId = routeProjectId || projectIdFromPathname(location.pathname);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  useContext(ChatContext); // Subscribe to context for reactive updates
  const [isNarrowView, setIsNarrowView] = useState(false);
  // Account public id (short, copy-able identifier like a Stripe id) shown
  // in the profile dropdown. Fetched lazily the first time the popover opens
  // so we don't spend a /credits/balance call on every page load.
  const [accountPublicId, setAccountPublicId] = useState(null);
  const [idCopied, setIdCopied] = useState(false);
  const isInsideProject = !!projectId;
  
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  // Lazily fetch the account public id the first time the profile popover
  // opens. Fails soft — if the call errors or the field is absent we just
  // render nothing (no broken UI).
  useEffect(() => {
    if (!isPopoverOpen || accountPublicId) return;
    let cancelled = false;
    Credits.getBalance()
      .then((res) => {
        if (!cancelled && res?.accountPublicId) setAccountPublicId(res.accountPublicId);
      })
      .catch(() => { /* no-op — id is optional decoration */ });
    return () => { cancelled = true; };
  }, [isPopoverOpen, accountPublicId]);

  const copyAccountId = async () => {
    if (!accountPublicId) return;
    try {
      await navigator.clipboard.writeText(accountPublicId);
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 1500);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isWorkspaceActive = () => {
    return location.pathname.startsWith('/mail') || 
           location.pathname.startsWith('/calendar') || 
           location.pathname.startsWith('/drive');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isDashboardActive = () => {
    // All Dashboard-related pages should highlight Dashboard tab
    return location.pathname === '/integrations' ||
           location.pathname === '/billing' ||
           location.pathname === '/contact';
  };

  // Get the order of all pages for navigation
  const getAllPageOrder = () => {
    return ['/mail', '/calendar', '/drive', '/integrations'];
  };

  const getCurrentPageIndex = () => {
    const pages = getAllPageOrder();
    const currentPath = isDashboardActive() ? '/integrations' : location.pathname;
    return pages.indexOf(currentPath);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const navigateToNextPage = (direction) => {
    const pages = getAllPageOrder();
    const currentIndex = getCurrentPageIndex();
    
    if (currentIndex === -1) return;
    
    let nextIndex;
    if (direction === 'left') {
      // Swipe left - go to previous page (left), with wrapping
      nextIndex = currentIndex - 1;
      if (nextIndex < 0) {
        nextIndex = pages.length - 1; // Wrap to last page
      }
    } else {
      // Swipe right - go to next page (right), with wrapping
      nextIndex = currentIndex + 1;
      if (nextIndex >= pages.length) {
        nextIndex = 0; // Wrap to first page
      }
    }
    
    navigate(pages[nextIndex]);
  };

  const handleLogout = async () => {
    setIsPopoverOpen(false);
    await menuController.close('sidebar-menu'); // Close mobile menu if open
    
    try {
      // Use the proper AuthService logout method
      AuthService.logout();
      console.log('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: manual cleanup and redirect
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = routesPath.login;
    }
  };

  const openSideMenu = async () => {
    await menuController.open('sidebar-menu');
  };

  // Detect when main content width becomes narrow (< 700px) due to chat sidebar
  useEffect(() => {
    const checkWidth = () => {
      const appMainContent = document.querySelector('.app-main-content');
      if (appMainContent) {
        const width = appMainContent.offsetWidth;
        setIsNarrowView(width < 700);
      }
    };

    // Check on mount
    checkWidth();

    // Check on window resize
    window.addEventListener('resize', checkWidth);

    // Check periodically for chat sidebar changes
    const interval = setInterval(checkWidth, 100);

    return () => {
      window.removeEventListener('resize', checkWidth);
      clearInterval(interval);
    };
  }, []);

  // Swipe gesture detection for both touchscreen and trackpad
  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;
    let trackpadDeltaX = 0;
    let isTrackpadSwipeActive = false;
    let trackpadSwipeTimeout = null;
    let justNavigated = false; // Block all events immediately after navigation
    let lastNavigationTime = 0;
    
    const minSwipeDistance = 200; // Minimum distance for a swipe to be registered
    const trackpadThreshold = 300; // Threshold for trackpad swipe (higher = less sensitive)
    const navigationCooldown = 1900; // ms to block after navigation (increased to prevent skipping pages)
    
    // Touch screen handlers
    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    };
    
    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;
      
      const horizontalDistance = touchEndX - touchStartX;
      const verticalDistance = Math.abs(touchEndY - touchStartY);
      
      // Only process horizontal swipes (not vertical scrolls)
      if (Math.abs(horizontalDistance) > minSwipeDistance && verticalDistance < 100) {
        // Navigate between all pages
        if (horizontalDistance > 0) {
          // Swipe right - go to next page (right)
          navigateToNextPage('right');
        } else {
          // Swipe left - go to previous page (left)
          navigateToNextPage('left');
        }
      }
    };
    
    // Trackpad/touchpad handler for horizontal swipes
    const handleWheel = (e) => {
      // Block all wheel events shortly after navigation
      const now = Date.now();
      if (justNavigated && now - lastNavigationTime < navigationCooldown) {
        return;
      }
      
      if (justNavigated && now - lastNavigationTime >= navigationCooldown) {
        justNavigated = false;
        trackpadDeltaX = 0;
        isTrackpadSwipeActive = false;
      }
      
      // ONLY detect horizontal swipes - ignore if vertical scrolling is dominant
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      
      // Only process if horizontal movement is significantly larger than vertical
      // This prevents accidental triggers during vertical scrolling
      if (absX > 0 && absX > absY * 3) { // Horizontal must be 3x larger than vertical
        if (!isTrackpadSwipeActive) {
          isTrackpadSwipeActive = true;
          trackpadDeltaX = 0;
        }
        
        trackpadDeltaX += e.deltaX;
        
        isWorkspaceActive();
        isDashboardActive();
        
        // Check immediately if we have enough movement for a swipe
        if (Math.abs(trackpadDeltaX) > trackpadThreshold) {
          // Navigate between all pages
          if (trackpadDeltaX > 0) {
            // Swipe right (trackpad) - go to next page (right)
            navigateToNextPage('right');
          } else {
            // Swipe left (trackpad) - go to previous page (left)
            navigateToNextPage('left');
          }
          justNavigated = true;
          lastNavigationTime = Date.now();
          trackpadDeltaX = 0;
          isTrackpadSwipeActive = false;
          return;
        }
        
        // Clear previous timeout
        if (trackpadSwipeTimeout) {
          clearTimeout(trackpadSwipeTimeout);
        }
        
        // Reset after inactivity
        trackpadSwipeTimeout = setTimeout(() => {
          trackpadDeltaX = 0;
          isTrackpadSwipeActive = false;
        }, 100);
      }
    };
    
    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('wheel', handleWheel, { passive: true });
    
    // Cleanup
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('wheel', handleWheel);
      if (trackpadSwipeTimeout) {
        clearTimeout(trackpadSwipeTimeout);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname, isWorkspaceActive, isDashboardActive, navigateToNextPage]);

  return (
    <>
      {/* Header */}
      <header className={`app-header ${isNarrowView ? 'narrow-view' : ''} ${isInsideProject ? 'in-project' : ''}`}>
        <div className="header-left">
          {!isNarrowView && !isInsideProject && <Logo onClick={() => navigate('/')} />}
          {/* Sidebar/menu trigger sits top-left — typical mobile pattern.
              On mobile in-project, the breadcrumb wraps to its own row
              below (see Header.css mobile rules). */}
          <button
            onClick={openSideMenu}
            className={`sidebar-menu-button ${isNarrowView ? '' : 'mobile-only'}`}
          >
            <SidebarIcon />
          </button>
        </div>

        {/* Breadcrumb portal — direct sibling of header-left so mobile
            CSS can place it on its own row below the action buttons.
            Was previously nested inside header-left, but that broke when
            we tried to give it a separate grid-area. */}
        {isInsideProject && <div id="header-breadcrumb-portal" />}

        {/* Centered Navigation Tabs */}
        <nav className="header-nav">
        </nav>

        <div className="header-right">
          {/* (removed) credit balance chip — see /billing for balance.
              The chip read as broken visually and duplicated the BillingPage
              balance hero. */}
          {/* Marketplace — explicit top-nav entry into the template catalog.
              Sits LEFT of the Invite/Docs/avatar block so the "explore"
              affordance is the first thing users see when scanning the
              header from project breadcrumbs rightward. Sidebar still has
              its own Marketplace link for users navigating from inside a
              workflow; the two paths don't conflict (same /marketplace route). */}
          <button
            type="button"
            className="header-docs-link"
            title="Marketplace"
            onClick={() => navigate(
              projectMarketplacePathForLocation(projectId, location.pathname)
            )}
          >
            <IonIcon icon={cubeOutline} />
            <span>Marketplace</span>
          </button>
          {/* Discord — ghost link with the official Clyde brand logo (white via
              CSS color) + "Discord" label. */}
          <a
            href="https://discord.gg/zgw5YPHrv"
            target="_blank"
            rel="noopener noreferrer"
            className="header-docs-link header-discord-link"
            title="Join our Discord"
          >
            <DiscordLogo className="header-discord-logo" />
            <span>Discord</span>
          </a>
          <a
            href="https://docs.zibby.app"
            target="_blank"
            rel="noopener noreferrer"
            className="header-docs-link"
            title="Documentation"
          >
            <IonIcon icon={bookOutline} />
            <span>Docs</span>
          </a>
          {/* Invite portal — ProjectLayout renders the actual button into
              this slot. Order: Marketplace · Docs · Invite · Avatar so
              "explore" (Marketplace/Docs) reads as a cluster and the
              project-scoped action (Invite) lives next to the avatar. */}
          {isInsideProject && <div id="header-actions-portal" />}
          {user && (
            <>
              <div 
                className="user-menu" 
                id="user-menu-trigger"
                onClick={() => setIsPopoverOpen(true)}
              >
                <div className={`user-avatar avatar-${getInitials(user.name || user.email)?.[0]?.toUpperCase() || 'U'}`}>
                  {getInitials(user.name || user.email)}
                </div>
              </div>
              
              <IonPopover
                trigger="user-menu-trigger"
                isOpen={isPopoverOpen}
                onDidDismiss={() => setIsPopoverOpen(false)}
                showBackdrop={true}
              >
                <div className="user-menu-popover">
                  <div className="user-info border-bottom">
                    <div className="user-info-name">{user.name}</div>
                    <div className="user-info-email">{user.email}</div>
                    {accountPublicId && (
                      <button
                        type="button"
                        className="user-info-id"
                        onClick={copyAccountId}
                        title="Copy account ID"
                      >
                        <span className="user-info-id-label">Account ID</span>
                        <span className="user-info-id-text">{accountPublicId}</span>
                        <IonIcon icon={idCopied ? checkmarkOutline : copyOutline} />
                      </button>
                    )}
                  </div>

                  {/* Account-section nav — same items as the desktop
                      NavigationMenu's Account/Support groups, surfaced
                      here so they're reachable from anywhere in the app
                      without opening the full sidebar. Same icons +
                      labels so users build one mental model. */}
                  <div className="user-menu-section border-bottom">
                    <div
                      className="menu-item"
                      onClick={() => { setIsPopoverOpen(false); navigate('/billing'); }}
                    >
                      <span>{t('nav.billing')}</span>
                      <IonIcon icon={cardOutline} />
                    </div>
                    <div
                      className="menu-item"
                      onClick={() => { setIsPopoverOpen(false); navigate('/usage'); }}
                    >
                      <span>{t('nav.usage')}</span>
                      <IonIcon icon={trendingUpOutline} />
                    </div>
                    <div
                      className="menu-item"
                      onClick={() => { setIsPopoverOpen(false); navigate('/settings/tokens'); }}
                    >
                      <span>Tokens</span>
                      <IonIcon icon={keyOutline} />
                    </div>
                  </div>

                  <div
                    className="menu-item"
                    onClick={() => { setIsPopoverOpen(false); setIsSettingsOpen(true); }}
                  >
                    <span>Settings</span>
                    <IonIcon icon={settingsOutline} />
                  </div>

                  <div className="menu-item logout-item" onClick={handleLogout}>
                    <span>{t('nav.logout')}</span>
                    <IonIcon icon={logOutOutline} className="logout-icon" />
                  </div>
                </div>
              </IonPopover>

              <SettingsModal
                isOpen={isSettingsOpen}
                onDismiss={() => setIsSettingsOpen(false)}
                user={user}
              />
            </>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
